import { StrictMode, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

import { createHttpTransport } from "@tdelivr/api-client";

import { arrivalFor, LeavingForTheProvider } from "./auth/arrival.tsx";
import { ConsoleAuthority, type SignInFailure } from "./auth/authority.ts";
import { isConfigured, readOidcConfiguration } from "./auth/configuration.ts";
import { useAuthentication } from "./auth/use-authentication.ts";
import { developmentSession } from "./development-session.ts";
import { tenantAdminPages } from "./navigation.ts";
import { pageIdIn, urlForPage } from "./page-location.ts";
import type { Session } from "./session.ts";
import { BrandDomains } from "./ui/BrandDomains.tsx";
import { ConfigurationBoard } from "./ui/ConfigurationBoard.tsx";
import { Customers } from "./ui/Customers.tsx";
import { Roles } from "./ui/Roles.tsx";
import { FeatureFlags } from "./ui/FeatureFlags.tsx";
import { Integrations } from "./ui/Integrations.tsx";
import { MessageTemplates } from "./ui/MessageTemplates.tsx";
import { PageOutlook } from "./ui/PageOutlook.tsx";
import { PlanEntitlements } from "./ui/PlanEntitlements.tsx";
import { PricingRateCards } from "./ui/PricingRateCards.tsx";
import { ServiceCatalogue } from "./ui/ServiceCatalogue.tsx";
import { SessionEnded } from "./ui/SessionEnded.tsx";
import { Shell } from "./ui/Shell.tsx";
import { SignIn } from "./ui/SignIn.tsx";
import { SignOutDialog } from "./ui/SignOutDialog.tsx";
import {
  DemandAnalytics,
  QualityAnalytics,
  RetentionAnalytics,
  SupplyAnalytics,
} from "./ui/AnalyticsBoards.tsx";
import { BookingChannels } from "./ui/BookingChannels.tsx";
import { DriverOnboarding } from "./ui/DriverOnboarding.tsx";
import { Merchants } from "./ui/Merchants.tsx";
import { Staff } from "./ui/Staff.tsx";
import { StateView } from "./ui/StateView.tsx";
import { TenantAudit } from "./ui/TenantAudit.tsx";
import { ZonesCoverage } from "./ui/ZonesCoverage.tsx";
import "./styles.css";

/**
 * The console, running.
 *
 * Two ways in, and only one of them exists in a production build.
 *
 * **Sign in.** OpenID Connect, authorization code flow with PKCE, against the provider named by
 * `VITE_OIDC_ISSUER`. The shape is not a choice: a browser application cannot hold a client secret,
 * so a public client with PKCE is the only correct option. `src/auth/` holds the whole of it and
 * `docs/adr/0018-the-console-signs-in-with-pkce.md` records why it is written out rather than
 * imported.
 *
 * An unauthenticated arrival does not draw a screen first. It goes straight to the provider's own
 * page, which is the sign-in page —
 * `docs/adr/0022-keycloaks-own-page-is-the-sign-in-page.md`, superseding ADR 0018 on that one point
 * and nothing else. `auth/arrival.tsx` holds the decision, including the two unauthenticated
 * arrivals that must be shown a page rather than redirected.
 *
 * **Development claims.** `VITE_ACCESS_TOKEN_CLAIMS`, read by `development-session.ts`, which is the
 * path the fidelity harness and the tests run on. Compiled out of a production build rather than
 * merely discouraged in it — see that file for the two mechanisms and the README for the measurement.
 *
 * Fourteen pages are routed. The one the design draws that is not falls through to
 * `PageOutlook`, which says what it is waiting for: `roles`, deferred by ADR 0016.
 *
 * `staff` and `channels` were the two where publishing a listing would not have drawn the board,
 * and both have since been drawn by writing the record instead — ADR 0084 and ADR 0091. That pair
 * is worth keeping in one sentence, because the restraint is what made each one's next step
 * obvious: R-39 served five listings whose rows are the rows their board draws and **stopped**
 * at these two rather than publishing an organisation-unit listing that would have reported this
 * page as served while answering a page of the wrong thing. The five R-39 served were
 * reachable and unbuilt for the length of one commit, which is what `PageOutlook`'s "waiting on a
 * screen" sentence was written for; R-40 drew them, R-55 served and drew `templates`,
 * `integrations` and `flags` in one change, so nothing is in that state now and the sentence stays
 * because the next listing to be served will put a page back into it.
 */

/**
 * Tenant, then section, then page.
 *
 * The tenant is first for the reason the chrome states it at all: an actor who can cross tenants must
 * always know which side of the boundary they are on. The groups are the design's own `NAV` groups,
 * so the trail reads the way the rail does — `East Africa Mobility Ltd › Govern › Tenant audit`.
 *
 * The design's own crumbs are `EAM › Admin › Govern › Tenant audit`, four deep, with a short tenant
 * name and the console's name between it and the group. Three of the four are kept: there is no short
 * tenant name anywhere on the platform (R-07, R-11), and a console that named itself in the trail
 * while the rail's head already says "Admin" would say it twice. Recorded as Q-23.
 */
function crumbsFor(tenantName: string, pageId: string): readonly string[] {
  const page = tenantAdminPages.find((candidate) => candidate.id === pageId);
  return [tenantName, page?.section ?? "Admin", page?.title ?? "Unknown page"];
}

interface ConsoleProps {
  readonly session: Session;
  /**
   * The bearer token for the next request, or undefined when there is no token to send.
   *
   * A function rather than a string, and asynchronous, because that is how a token stays fresh: the
   * authority refreshes one that is about to expire before handing it over, so a request never goes
   * out carrying a token a service will refuse. `createHttpTransport` already accepted a promise
   * here, so nothing in `packages/api-client` changed.
   *
   * Absent on the development path, which has no token at all. That is what the harness runs with
   * and what it has always run with: the stubs answer without looking at an authorization header.
   */
  readonly accessToken?: () => Promise<string | undefined>;
  readonly onSignOut?: () => void;
  /** Rendered in place of the page, with the chrome intact, as the design draws it. */
  readonly expired?: ReactNode;
}

function Console({ session, accessToken, onSignOut, expired }: ConsoleProps): ReactNode {
  /*
   * The open page, in the address bar.
   *
   * It was React state alone, which meant every load opened the live map and there was no way to say
   * which page an operator had asked for — including to the sign-in flow, which has to send them back
   * to it. `page-location.ts` is the whole of the mechanism.
   */
  const [pageId, setPageId] = useState<string>(() => pageIdIn(window.location.href));

  useEffect(() => {
    // Back and forward move between pages rather than out of the console, now that pages are
    // addressable. Without this the browser's back button leaves a single-page application that has
    // pushed history entries, which is the worst of both.
    const onPopState = (): void => {
      setPageId(pageIdIn(window.location.href));
    };
    window.addEventListener("popstate", onPopState);
    return (): void => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const navigate = useCallback((next: string) => {
    window.history.pushState(null, "", urlForPage(window.location.href, next));
    setPageId(next);
  }, []);

  /*
   * One transport for the session, and memoised.
   *
   * The token and the tenant are read per request rather than captured, so a refreshed token is
   * picked up without rebuilding anything the screens hold. Memoised because the header's inbox
   * effect depends on the transport's identity, and the authentication state above this component
   * now changes — a fresh object on every render would reload the inbox on every render.
   */
  const transport = useMemo(
    () =>
      createHttpTransport({
        // The page's own origin, not "/". The transport resolves each path with `new URL(path, base)`,
        // and `new URL("v1/…", "/")` throws "Invalid base URL" because a bare slash is not an absolute
        // URL — so the default configuration could not make a single request, and every screen rendered
        // its error state. Found the first time the console was rendered by the fidelity check rather
        // than reasoned about, which is the argument for rendering it.
        //
        // Same origin is also the right default once the gateway exists: Document 17 WEB-0743 forbids
        // exposing the services directly, so the console and its API are one origin in every environment
        // that is not a developer's machine.
        baseUrl: import.meta.env.VITE_PLATFORM_URL ?? window.location.origin,
        tenantId: () => session.tenantId,
        ...(accessToken === undefined ? {} : { authorization: accessToken }),
      }),
    [session.tenantId, accessToken],
  );

  return (
    <Shell
      session={session}
      currentPageId={pageId}
      onNavigate={navigate}
      transport={transport}
      {...(onSignOut === undefined ? {} : { onSignOut })}
    >
      {expired !== undefined ? (
        expired
      ) : pageId === "audit" ? (
        <TenantAudit transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "zones" ? (
        <ZonesCoverage transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "brand" ? (
        <BrandDomains transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "catalogue" ? (
        <ServiceCatalogue transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "policies" ? (
        <ConfigurationBoard
          transport={transport}
          crumbs={crumbsFor(session.tenantName, pageId)}
          variant="policies"
        />
      ) : pageId === "config" ? (
        /*
         * The same component as `policies`, and the same operation behind it.
         *
         * `tests/page-support.test.ts` measures both of these pages onto `TEN-02
         * /v1/configuration`: both are boards of this tenant's configuration entries, and what
         * separates them in the design is a third column neither can fill. Two components fetching
         * one listing would be two places for one decision.
         */
        <ConfigurationBoard
          transport={transport}
          crumbs={crumbsFor(session.tenantName, pageId)}
          variant="config"
        />
      ) : pageId === "plan" ? (
        <PlanEntitlements
          transport={transport}
          crumbs={crumbsFor(session.tenantName, pageId)}
          tenantId={session.tenantId}
        />
      ) : pageId === "pricing" ? (
        <PricingRateCards
          transport={transport}
          crumbs={crumbsFor(session.tenantName, pageId)}
          tenantId={session.tenantId}
        />
      ) : pageId === "customers" ? (
        <Customers transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "roles" ? (
        <Roles
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "merchants" ? (
        <Merchants
          transport={transport}
          scopes={session.scopes}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "driver-onboarding" ? (
        <DriverOnboarding
          transport={transport}
          scopes={session.scopes}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "staff" ? (
        <Staff
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "channels" ? (
        /*
         * ADR 0091, and the second of this console's two "a listing would not have helped" pages to
         * be drawn by writing the record instead. Its own recorded reason was right about both
         * halves, which three of the four re-measured across this plan were not.
         */
        <BookingChannels
          transport={transport}
          session={session}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "templates" ? (
        <MessageTemplates transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "integrations" ? (
        <Integrations transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "flags" ? (
        <FeatureFlags transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : /*
       * The four analytics boards, which no design file draws — Q-186.
       *
       * They sit at the end of this chain rather than beside the pages they most resemble,
       * because the order here is the design file's page order and these are not in it. A reader
       * comparing this chain against `17 WP16 Tenant Admin.dc.html` should find its fifteen in
       * its order and then these, rather than finding its order quietly rearranged.
       */
      pageId === "analytics-demand" ? (
        <DemandAnalytics
          transport={transport}
          session={session}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "analytics-supply" ? (
        <SupplyAnalytics
          transport={transport}
          session={session}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "analytics-retention" ? (
        <RetentionAnalytics
          transport={transport}
          session={session}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "analytics-quality" ? (
        <QualityAnalytics
          transport={transport}
          session={session}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : (
        <PageOutlook session={session} />
      )}
    </Shell>
  );
}

/**
 * Everything above the console: leaving to sign in, the code exchange, the expiry and the sign-out.
 *
 * One switch over one state, which is the point of `ConsoleAuthority` holding all of it. The question
 * "is this operator signed in" has exactly one answer at any moment, and the chrome, the transport and
 * this switch all read it from the same place.
 *
 * Exactly one of its branches leaves this page: `signed_out`, and only when `arrivalFor` says the
 * arrival has nothing to report. Every other branch draws something. That asymmetry is the whole of
 * ADR 0022's caution — a state that is unauthenticated *because something went wrong* must be read,
 * not retried, or the console redirects into the same failure for as long as the browser lets it.
 */
function Authenticated({ authority }: { readonly authority: ConsoleAuthority }): ReactNode {
  const state = useAuthentication(authority);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  /**
   * Whether the operator has dismissed the sign-in screen's failure.
   *
   * The design's "Use a different account" returns to the start step. There is nothing to ask the
   * authority for — the failure is already final — so it is this component's state rather than the
   * authority's, which keeps the authority a statement about the session rather than about a screen.
   *
   * It returns to a page rather than to the provider on purpose. The operator has just been told why
   * a sign-in failed; the next move is theirs.
   */
  const [dismissedFailure, setDismissedFailure] = useState(false);

  const providerName = authority.providerName;
  const accessToken = useCallback(() => authority.accessToken(), [authority]);
  /*
   * Leave for the provider, from a button an operator pressed.
   *
   * No argument any more. It carried the email from the design's first step as `login_hint`, and ADR
   * 0022 removed that step: the provider's own form asks for the address, so collecting it here only
   * to send it there was the hop the operator decided against.
   */
  const signIn = useCallback(() => {
    setDismissedFailure(false);
    void authority.beginSignIn();
  }, [authority]);

  switch (state.kind) {
    case "starting":
      // The whole page, before anything has been decided. The design's loading state, which this
      // console renders in one place for every screen, centred because here it is the page.
      return (
        <div className="boot">
          <StateView state={{ kind: "loading" }} purpose="The console is starting." />
        </div>
      );

    case "signed_out":
      /*
       * The one render site that became a redirect, and the only one.
       *
       * Nobody is signed in and nothing has gone wrong, so there is nothing to say that the
       * provider's own page does not say better. `arrivalFor` decides, rather than this component
       * redirecting on sight, because two unauthenticated arrivals must still be given a page: the
       * operator who has just signed out, who would otherwise be sent back to the form they left,
       * and the tab that has already been to the provider and returned with no answer, which would
       * otherwise bounce between the two for ever. Both then land on the step below, which is the
       * design's first step with its email field removed.
       */
      return arrivalFor(state).kind === "to_the_provider" ? (
        <LeavingForTheProvider authority={authority} />
      ) : (
        <SignIn
          step="start"
          afterSignOut={state.afterSignOut}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "authenticating":
      // Left as a page, deliberately. This is the redirect in flight — discovery running, or the
      // code coming back to be exchanged — and it is what the operator sees for the moment before
      // the browser leaves. A redirect here would be a redirect during a redirect.
      return (
        <SignIn
          step="authenticating"
          afterSignOut={false}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "failed":
      /*
       * Left as a page, and this is the one that matters most.
       *
       * A sign-in that did not finish is exactly the state in which redirecting would loop: the
       * console would leave for the provider, fail the same way, and leave again, so an operator
       * whose account is not linked would never get to read the sentence telling them so. The
       * failure is drawn and the only way back out to the provider is a button they press.
       */
      return (
        <SignIn
          step={dismissedFailure ? "start" : "failed"}
          afterSignOut={false}
          providerName={providerName}
          failure={state.failure}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "expired":
      return (
        <Console
          session={state.session}
          accessToken={accessToken}
          expired={
            <SessionEnded
              tenantName={state.session.tenantName}
              endedAt={state.endedAt}
              lastedMs={state.lastedMs}
              onSignInAgain={() => {
                void authority.beginSignIn();
              }}
            />
          }
        />
      );

    case "signed_in":
      return (
        <>
          <Console
            session={state.session}
            accessToken={accessToken}
            onSignOut={() => setConfirmingSignOut(true)}
          />
          {confirmingSignOut ? (
            <SignOutDialog
              onDismiss={() => setConfirmingSignOut(false)}
              onConfirm={() => {
                setConfirmingSignOut(false);
                void authority.signOut();
              }}
            />
          ) : null}
        </>
      );
  }
}

/**
 * A build with no identity provider configured, said in the design's failure card.
 *
 * Also left as a page. There is no authorization endpoint to redirect to — that is what the card
 * says — so this is the state where a redirect is not merely a loop but impossible.
 */
function unconfigured(missing: readonly string[]): SignInFailure {
  return {
    code: "console_not_configured",
    headline: "This console has no identity provider configured",
    detail: `It was built without ${missing.join(" and ")}, so there is nowhere to send you to sign in. A platform administrator sets those when the console is built or served. Retrying won't change this.`,
    retryable: false,
  };
}

const root = document.getElementById("root");
if (root === null) throw new Error("The console has no mount point");

/*
 * Rendered synchronously, in every case.
 *
 * Not an aesthetic preference. Module scripts run before `DOMContentLoaded`, and the fidelity
 * harness asks whether a navigation button `isVisible()` immediately after that event without
 * waiting for it — so a bootstrap that awaited anything before its first render would have the
 * harness conclude the rail was absent and start opening a drawer that is not there at 1440. The
 * asynchronous part of signing in happens after the first paint, behind the design's loading state.
 */
const development = import.meta.env.DEV ? developmentSession() : undefined;

if (development !== undefined) {
  createRoot(root).render(
    <StrictMode>
      <Console session={development} />
    </StrictMode>,
  );
} else {
  const configuration = readOidcConfiguration(import.meta.env, window.location.origin);
  if (!isConfigured(configuration)) {
    createRoot(root).render(
      <StrictMode>
        <SignIn
          step="failed"
          afterSignOut={false}
          providerName="your organisation's sign-in"
          failure={unconfigured(configuration.missing)}
          onContinue={() => {}}
          onStartAgain={() => {}}
        />
      </StrictMode>,
    );
  } else {
    const authority = new ConsoleAuthority({
      configuration,
      fetch: globalThis.fetch.bind(globalThis),
      crypto: globalThis.crypto,
      // Per tab. Two tabs signing in at once must not read each other's code verifier, and a
      // verifier left in `localStorage` would outlive the browser session it belongs to.
      storage: window.sessionStorage,
      currentUrl: (): string => window.location.href,
      replaceUrl: (url: string): void => {
        window.history.replaceState(null, "", url);
      },
      navigate: (url: string): void => {
        window.location.assign(url);
      },
      now: (): number => Date.now(),
    });

    createRoot(root).render(
      <StrictMode>
        <Authenticated authority={authority} />
      </StrictMode>,
    );
    // After the first render, so the loading state is on screen while discovery and the code
    // exchange happen.
    void authority.start();
  }
}
